import type { dashboard_element, display_config, guide_tile_config } from "@/lib/types";
import { TREND_VARIANTS } from "../constants";
import type { TrendVariant } from "../types";

export const isTrendVariant = (variant: unknown): variant is TrendVariant =>
  TREND_VARIANTS.some((v) => v.value === variant);

/**
 * The Trends variants (raw level, annual, 3-month and monthly % change) among
 * a series' variants and its default, in the page's usual order. Any other
 * variant is dropped.
 */
export function trendVariantsIn(variants: readonly (string | null | undefined)[]): TrendVariant[] {
  const listed = new Set(variants.filter(Boolean));
  return TREND_VARIANTS.map((v) => v.value).filter((v) => listed.has(v));
}

/**
 * A dashboard gauge as Trends takes it: its series, and the variant saved on
 * the gauge when Trends can show it. Everything else about the series (name,
 * formatting, supported variants, tier) is Trends' own lookup.
 */
export interface DashboardSeries {
  displayId: string;
  variant?: TrendVariant;
}

const fromDisplayConfig = (config: display_config | string): DashboardSeries => {
  if (typeof config === "string") return { displayId: config };
  // Only `variant` is a saved choice. `view_name` is filled in as "rawlevel"
  // for every gauge stored as a plain id, so it says nothing; gauges ignore it.
  return { displayId: config.display_id, ...(isTrendVariant(config.variant) ? { variant: config.variant } : {}) };
};

/**
 * Every gauge on a dashboard in order, including those inside guide tiles,
 * each series once (its first gauge wins). Content tiles are skipped.
 */
export function seriesFromDashboard(elements: dashboard_element[]): DashboardSeries[] {
  const all = elements.flatMap((element): DashboardSeries[] => {
    if (typeof element === "string") return [fromDisplayConfig(element)];
    if ("headerTitle" in element) return ((element as guide_tile_config).displayConfigs ?? []).map(fromDisplayConfig);
    if ("display_id" in element) return [fromDisplayConfig(element)];
    return [];
  });

  const seen = new Set<string>();
  return all.filter(({ displayId }) => {
    if (!displayId || seen.has(displayId)) return false;
    seen.add(displayId);
    return true;
  });
}

// Short codes keep the URL readable with a few dozen series on it.
const VARIANT_CODES: Record<TrendVariant, string> = {
  rawlevel: "r",
  annual_pct_change: "a",
  quarterly_pct_change: "q",
  monthly_pct_change: "m",
};
const CODE_VARIANTS = Object.fromEntries(
  Object.entries(VARIANT_CODES).map(([variant, code]) => [code, variant as TrendVariant]),
);

/**
 * `?series=` value for the Trends page, comma-separated: each series as `ID`,
 * or `ID:code` with its saved variant in short code (`QUITS:a` for annual % change).
 */
export function encodeDashboardSeries(series: DashboardSeries[]): string {
  return series.map(({ displayId, variant }) => (variant ? `${displayId}:${VARIANT_CODES[variant]}` : displayId)).join(",");
}

/**
 * The series in a `?series=` value, each once. An unknown variant code is
 * dropped. Older links carried more codes after the variant
 * (`QUITS:a:ram:r:l`); only the id and variant are read.
 */
export function decodeDashboardSeries(param: string | null | undefined): DashboardSeries[] {
  if (!param) return [];
  const seen = new Set<string>();
  return param.split(",").flatMap((part): DashboardSeries[] => {
    const [rawId, code] = part.trim().split(":");
    const displayId = rawId?.trim();
    if (!displayId || seen.has(displayId)) return [];
    seen.add(displayId);
    const variant = CODE_VARIANTS[code ?? ""];
    return [{ displayId, ...(variant ? { variant } : {}) }];
  });
}

/**
 * A page in this app to go back to, or null. Only a same-site path counts, so
 * a hand-edited `?from=` can't send the viewer to another site.
 */
export function safeReturnPath(path: string | null | undefined): string | null {
  if (!path || !path.startsWith("/") || path.startsWith("//") || path.startsWith("/\\")) return null;
  return path;
}

/** The page the viewer is on now, as a path to come back to. */
export const currentPagePath = (): string => `${window.location.pathname}${window.location.search}`;

/**
 * Where the dashboard's "See as Trend" button goes: its series, its name for
 * the page title, and the page it was opened from, for the way back.
 */
export function trendViewHref(series: DashboardSeries[], title?: string, from?: string): string {
  const back = safeReturnPath(from);
  return `/trends?${new URLSearchParams({
    series: encodeDashboardSeries(series),
    ...(title ? { title } : {}),
    ...(back ? { from: back } : {}),
  })}`;
}

/** "See as Trend" on a shared link: the link's own Trends page, with the same params. */
export function sharedTrendViewHref(slug: string, series: DashboardSeries[], title?: string): string {
  return `/shared-guides/${encodeURIComponent(slug)}${trendViewHref(series, title)}`;
}
